import { useContext } from 'react';
import { AssetContext, AssetContextValue } from 'src/contexts/AssetContext';

/**
 * Hook for accessing the asset context which manages the state of campaign assets
 * (background image, logo, video trailer).
 *
 * @throws {Error} If used outside of an AssetContextProvider
 * @returns {AssetContextValue} The asset context value containing asset states and update method
 *
 * @example
 * // Basic usage
 * const { assetStates, updateAssetState } = useAssetContext();
 *
 * // Check if background image is uploaded
 * const isBackgroundReady = assetStates[AssetType.BACKGROUND_IMAGE].status === AssetStatus.COMPLETED;
 *
 * @example
 * // Updating asset state
 * const { updateAssetState } = useAssetContext();
 *
 * // Update logo status
 * updateAssetState(AssetType.LOGO, {
 *   status: AssetStatus.PENDING_UPLOAD,
 *   error: undefined
 * });
 *
 * @example
 * // Error handling
 * const { assetStates } = useAssetContext();
 *
 * // Check for errors
 * const hasError = assetStates[AssetType.VIDEO_TRAILER].error !== undefined;
 * if (hasError) {
 *   console.error(assetStates[AssetType.VIDEO_TRAILER].error);
 * }
 */
export const useAssetContext = (): AssetContextValue => {
  const context = useContext(AssetContext);

  if (!context) {
    throw new Error('useAssetContext must be used within an AssetContextProvider');
  }

  return context;
};
